import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCohortRetention } from '../api/client';
import { formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { InfoTip } from '../components/InfoTip';

interface CohortData {
  registrationDate: string;
  d1: number;
  d7: number;
  d30: number;
  d90: number;
}

export const CohortRetention: React.FC = () => {
  const { t } = useTranslation();
  const [month, setMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  );
  const [channel, setChannel] = useState<string>('all');
  const [cohortData, setCohortData] = useState<CohortData[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCohortData();
  }, [month, channel]);

  const loadCohortData = async () => {
    setLoading(true);
    try {
      const response = await getCohortRetention({ cohort_month: month });
      const rows = response.data || [];
      setCohortData(rows.map((r: any) => ({
        registrationDate: r.cohort_date || r.date || '',
        d1: r.d1_retention || 0,
        d7: r.d7_retention || 0,
        d30: r.d30_retention || 0,
        d90: r.d90_retention || 0,
      })));
    } catch (error) {
      console.error('Failed to load cohort data:', error);
      setCohortData([]);
    } finally {
      setLoading(false);
    }
  };

  const getColorForRetention = (value: number): string => {
    if (value >= 50) return 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100';
    if (value >= 20) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-100';
    return 'bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100';
  };

  const channels = ['all', 'organic', 'paid', 'referral', 'affiliate'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {t('cohortRetention.title')}
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          {t('cohortRetention.subtitle')}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-900 dark:text-white">
            {t('cohortRetention.selectMonth')}
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="mt-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-900 dark:text-white">
            {t('cohortRetention.acquisitionChannel')}
          </label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="mt-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          >
            {channels.map((ch) => (
              <option key={ch} value={ch}>
                {t(`cohortRetention.channel${ch.charAt(0).toUpperCase() + ch.slice(1)}`)}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={loadCohortData}
          disabled={loading}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-medium text-slate-900 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
        >
          {loading ? t('cohortRetention.loading') : t('cohortRetention.refresh')}
        </button>
      </div>

      {/* Cohort Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">
                <InfoTip tip={metricTip('cohortDate')}>{t('cohortRetention.columnRegistrationDate')}</InfoTip>
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900 dark:text-white">
                <InfoTip tip={metricTip('d1')} className="justify-center">{t('cohortRetention.columnD1')}</InfoTip>
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900 dark:text-white">
                <InfoTip tip={metricTip('d7')} className="justify-center">{t('cohortRetention.columnD7')}</InfoTip>
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900 dark:text-white">
                <InfoTip tip={metricTip('d30')} className="justify-center">{t('cohortRetention.columnD30')}</InfoTip>
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900 dark:text-white">
                <InfoTip tip={metricTip('d90')} className="justify-center">{t('cohortRetention.columnD90')}</InfoTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {cohortData.map((row, index) => (
              <tr
                key={index}
                className="border-b border-slate-200 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50"
              >
                <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                  {new Date(row.registrationDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`
                      inline-flex items-center justify-center rounded-full px-3 py-1 font-semibold
                      ${getColorForRetention(row.d1)}
                    `}
                  >
                    {formatNumber(row.d1, 1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`
                      inline-flex items-center justify-center rounded-full px-3 py-1 font-semibold
                      ${getColorForRetention(row.d7)}
                    `}
                  >
                    {formatNumber(row.d7, 1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`
                      inline-flex items-center justify-center rounded-full px-3 py-1 font-semibold
                      ${getColorForRetention(row.d30)}
                    `}
                  >
                    {formatNumber(row.d30, 1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`
                      inline-flex items-center justify-center rounded-full px-3 py-1 font-semibold
                      ${getColorForRetention(row.d90)}
                    `}
                  >
                    {formatNumber(row.d90, 1)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 rounded bg-green-100 dark:bg-green-900/30" />
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {t('cohortRetention.legendGood')}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 rounded bg-yellow-100 dark:bg-yellow-900/30" />
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {t('cohortRetention.legendFair')}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 rounded bg-red-100 dark:bg-red-900/30" />
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {t('cohortRetention.legendPoor')}
          </span>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 dark:border-accent-900/50 dark:bg-accent-900/20">
        <p className="text-sm text-accent-800 dark:text-accent-200">
          {t('cohortRetention.infoText')}
        </p>
      </div>
    </div>
  );
};
